import { useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import { setHumanSharePercentage } from '../../state/createProjectSlice';
import { selectHumanSharePercentages, selectSelectedAnnotators } from '../../state/selector';
import { isValidSharePercentage, summarizeCustomShares } from '../../utils/agentAllocation';

// TODO: cleanup with cp_annotation_createproject_agent ff

interface AgentCustomAllocationRow {
  id: string;
  label: string;
  /** The share as typed; empty until the manager enters one. */
  rawShare: string;
  /** null while the share is not a valid whole number. */
  encounterCount: number | null;
}

interface UseAgentCustomAllocationResults {
  rows: AgentCustomAllocationRow[];
  total: number;
  isValid: boolean;
  changeShare: (annotatorId: string, value: string) => void;
}

/**
 * The human rows of the Custom table on step 6 while the annotation agent is on, with each
 * human's encounters computed from n the way the server computes them.
 */
export const useAgentCustomAllocation = (
  encounterCount: number
): UseAgentCustomAllocationResults => {
  const dispatch = useAppDispatch();
  const selectedAnnotators = useAppSelector(selectSelectedAnnotators);
  const humanSharePercentages = useAppSelector(selectHumanSharePercentages);

  const { rows, total, isValid } = useMemo(() => {
    const rawShares = selectedAnnotators.map(({ id }) => humanSharePercentages[id] ?? '');
    const summary = summarizeCustomShares(encounterCount, rawShares);
    return {
      rows: selectedAnnotators.map(({ id, label }, index) => ({
        id,
        label,
        rawShare: rawShares[index],
        encounterCount: summary.rows[index].encounterCount,
      })),
      total: summary.total,
      isValid: summary.isValid,
    };
  }, [encounterCount, selectedAnnotators, humanSharePercentages]);

  // Only a valid share, or an empty field while typing, is accepted: a decimal, a sign, a
  // letter or a leading zero leaves the field as it was.
  const changeShare = (annotatorId: string, value: string) => {
    if (value === '' || isValidSharePercentage(value)) {
      dispatch(setHumanSharePercentage({ annotatorId, value }));
    }
  };

  return { rows, total, isValid, changeShare };
};
